/* eslint-disable @typescript-eslint/no-floating-promises */
import {SiteStore} from '@wix/wixstores-client-storefront-sdk';
import {clickOnCheckoutParams, errorInCheckoutParams} from '@wix/bi-logger-ecom-platform-data/v2/types';
import {ContinueShoppingBiOrigin, EMPTY_CART_ID} from '../../common/constants';
import {createOOIWebBILogger} from '@wix/wixstores-client-core';
import {getCatalogAppIds, getItemsCount, getItemTypes, toBiAmount} from '../utils/bi.utils';
import {
  cancelClickedInSelectYourShippingDestinationPopupSf,
  cartCartSideCartPresentedToUoUCartButtonSrc130Evid150,
  cartCartSideCartPresentedToUoUSrc130Evid149,
  cartCheckoutClickToOpenCloseMoreDetailsSrc130Evid190,
  cartEstimateTotalsErrorClickOnTryAgainSrc130Evid147,
  cartEstimateTotalsErrorShownSrc130Evid146,
  cartMinimumOrderMessageIsShownInCart,
  cartNotePresentedToTheUoUSrc130Evid157,
  cartPromoCodePresentedToTheUoUInCartSideCartSrc130Evid156,
  cartSideCartErrorPresentedInCartSideCartSrc130Evid131,
  clickOnAddNoteToSellerSf,
  clickOnCheckout,
  clickOnCheckoutWithEWallet,
  clickOnContinueShoppingFromCart,
  clickOnShippingDestinationInCartPageSf,
  clickToAddCoupon,
  clickToViewCartPage,
  deleteNoteToSellerSf,
  errorInCheckout,
  errorWhenApplyingACoupon,
  saveNoteToSellerSf,
  shippingShippingOptionsPresentedInCheckoutCartSrc130Evid183,
  shippingShippingOptionsSelectedSrc130Evid186,
  updateClickedInSelectYourShippingDestinationPopupSf,
  viewCartPageSf,
  ecomTestBiSrc130Evid160,
  updatedCartItemQuantitySf,
  clickApplyCoupon,
  removedProductFromCartSf,
  removeACoupon,
  checkoutCartCartSummaryPresentedSrc130Evid226,
} from '@wix/bi-logger-ecom-platform-data/v2';
import {CartModel} from '../models/Cart/Cart.model';
import {CartSummaryModel} from '../models/CartSummary/CartSummary.model';
import {CartLayout} from '../../types/app.types';
import {SPECS} from '../specs';
import {DeliverySectionModel} from '../models/DeliverySection.model';
import {ViewerContext} from '@wix/ecom-platform-storefront-common';
import {Logger} from '@wix/web-bi-logger/dist/src/types';
import {IWidgetControllerConfig} from '@wix/native-components-infra';

type SetDestinationBIData = {
  cartId: string;
  itemType: string;
  shippingDestinationCountry?: string;
  shippingDestinationState?: string;
};

export class BIService {
  private readonly siteStore: SiteStore;
  private readonly origin: string;
  private readonly viewerContext: ViewerContext;
  private readonly biLogger: Logger;
  private readonly controllerConfig: IWidgetControllerConfig;

  constructor({
    siteStore,
    origin,
    viewerContext,
    controllerConfig,
  }: {
    siteStore: SiteStore;
    origin: string;
    viewerContext: ViewerContext;
    controllerConfig: IWidgetControllerConfig;
  }) {
    this.siteStore = siteStore;
    this.origin = origin;
    this.viewerContext = viewerContext;
    this.controllerConfig = controllerConfig;
    this.biLogger = this.viewerContext.isExperimentEnabled(SPECS.MoveCartToViewerContext)
      ? this.createBILogger(controllerConfig, viewerContext)
      : siteStore.webBiLogger;
  }

  private baseBiParams(cartModel: CartModel): {
    origin: string;
    cartId?: string;
    purchaseFlowId?: string;
    cart_revision?: string;
  } {
    return {
      origin: this.origin,
      cartId: !cartModel || cartModel.id === EMPTY_CART_ID ? undefined : cartModel.id,
      purchaseFlowId: cartModel?.purchaseFlowId,
      cart_revision: cartModel?.revision,
    };
  }

  public clickOnShippingDestinationInCartPageSf(cartModel: CartModel): void {
    const biParams = {
      ...this.baseBiParams(cartModel),
      ...BIService.setDestinationBIData(cartModel),
    };

    this.biLogger.report(
      clickOnShippingDestinationInCartPageSf({
        ...biParams,
        catalogAppId: getCatalogAppIds(cartModel),
        checkoutId: cartModel.checkoutId,
      })
    );
  }

  public clickOnCheckoutWithEWallet(cartModel: CartModel): void {
    const params = {
      ...this.baseBiParams(cartModel),
      ...BIService.setDestinationBIData(cartModel),
      itemsCount: getItemsCount(cartModel),
      productsList: JSON.stringify(
        cartModel.lineItems.map((lineItem) => ({
          id: lineItem.source.catalogReference.catalogItemId,
          quantity: lineItem.quantityInfo.quantity,
        }))
      ),
      checkoutId: cartModel?.checkoutId,
      catalogAppId: getCatalogAppIds(cartModel),
    };

    this.biLogger.report(
      clickOnCheckoutWithEWallet({
        ...params,
      })
    );
  }

  public updateClickedInSelectYourShippingDestinationPopupSf(
    cartModel: CartModel,
    changedDestination: {country: string; subdivision: string; zipCode: string}
  ): void {
    const biParams = {
      ...this.baseBiParams(cartModel),
      shippingDestinationCountry: changedDestination.country,
      shippingDestinationState: changedDestination.subdivision,
      hasZipCode: !!changedDestination.zipCode,
      cartId: cartModel.id,
      itemType: getItemTypes(cartModel),
    };

    this.biLogger.report(
      updateClickedInSelectYourShippingDestinationPopupSf({
        ...biParams,
        catalogAppId: getCatalogAppIds(cartModel),
        checkoutId: cartModel.checkoutId,
      })
    );
  }

  public errorInCheckoutSf(cartModel: CartModel): void {
    const {deliveryInfo} = cartModel;
    const subdivision = deliveryInfo?.address.subdivision
      ? ` - ${deliveryInfo.address.subdivision}`
      : /* istanbul ignore next */ '';

    const destinationStr = `${deliveryInfo?.address.country}${subdivision}`;
    const params: errorInCheckoutParams = {
      ...this.baseBiParams(cartModel),
      field: 'destination',
      message: deliveryInfo?.address.country
        ? `cannot ship to destination ${destinationStr}`
        : 'cannot ship to destination',
      stage: 'cart',
    };
    this.biLogger.report(errorInCheckout(params));
  }

  public cancelClickedInSelectYourShippingDestinationPopupSf(cartModel: CartModel): void {
    const biParams = {
      ...this.baseBiParams(cartModel),
      itemType: getItemTypes(cartModel),
    };

    this.biLogger.report(
      cancelClickedInSelectYourShippingDestinationPopupSf({
        ...biParams,
        catalogAppId: getCatalogAppIds(cartModel),
        checkoutId: cartModel?.checkoutId,
      })
    );
  }

  public cartClickOnContinueShoppingSf(cartModel: CartModel, origin: ContinueShoppingBiOrigin): void {
    const biParams = {
      ...this.baseBiParams(cartModel),
      origin,
      catalogAppId: getCatalogAppIds(cartModel),
      checkoutId: this.viewerContext.isExperimentEnabled(SPECS.CallCartV2FromCartComponents)
        ? undefined
        : cartModel?.checkoutId,
      itemType: getItemTypes(cartModel),
    };
    this.biLogger.report(clickOnContinueShoppingFromCart(biParams));
  }

  public clickToViewCartPage(cartModel: CartModel): void {
    const biParams = {
      ...this.baseBiParams(cartModel),
      origin: this.origin,
      itemsCount: getItemsCount(cartModel),
      itemType: getItemTypes(cartModel),
      catalogAppId: getCatalogAppIds(cartModel),
      checkoutId: this.viewerContext.isExperimentEnabled(SPECS.CallCartV2FromCartComponents)
        ? undefined
        : cartModel?.checkoutId,
      isNavigateCart: true,
    };

    this.biLogger.report(clickToViewCartPage(biParams));
  }

  public minimumOrderMessageIsShownInCart(
    cartModel: CartModel,
    cartSummary: CartSummaryModel,
    buttonEnabled: boolean
  ): void {
    const biParams = {
      ...this.baseBiParams(cartModel),
      currency: cartModel.businessInfo.currencyCode,
      minimumValueLong: toBiAmount(cartSummary.minimumOrderAmount.minAmount.convertedAmount),
      diffLong: toBiAmount(cartSummary.minimumOrderAmount.additionalAmount.convertedAmount),
      buttonEnabled,
      checkoutId: this.viewerContext.isExperimentEnabled(SPECS.CallCartV2FromCartComponents)
        ? undefined
        : cartModel?.checkoutId,
      catalogAppId: getCatalogAppIds(cartModel),
    };

    this.biLogger.report(cartMinimumOrderMessageIsShownInCart(biParams));
  }

  public clickOnAddNoteToSellerSf(cartModel: CartModel): void {
    this.biLogger.report(
      clickOnAddNoteToSellerSf({
        ...this.baseBiParams(cartModel),
        catalogAppId: getCatalogAppIds(cartModel),
        checkoutId: cartModel?.checkoutId,
      })
    );
  }

  public clickOnApplyPromotionalCodeSf(cartModel: CartModel): void {
    this.biLogger.report(
      clickToAddCoupon({
        ...this.baseBiParams(cartModel),
      })
    );
  }

  public errorWhenApplyingACouponSf(cartModel: CartModel, couponCode: string, errorCode: string): void {
    this.biLogger.report(
      errorWhenApplyingACoupon({
        ...this.baseBiParams(cartModel),
        couponCode,
        errorCode,
      })
    );
  }

  public errorPresentedInCartSideCart(errorMessage: string, cartModel: CartModel): void {
    this.biLogger.report(
      cartSideCartErrorPresentedInCartSideCartSrc130Evid131({
        ...this.baseBiParams(cartModel),
        errorMessage,
      })
    );
  }

  public updateBuyerNote = (cartModel: CartModel, hasNote: boolean): void => {
    const catalogAppId = getCatalogAppIds(cartModel);
    hasNote
      ? this.biLogger.report(
          saveNoteToSellerSf({
            ...this.baseBiParams(cartModel),
            is_empty: false,
            catalogAppId,
            checkoutId: cartModel.checkoutId,
          })
        )
      : this.biLogger.report(
          deleteNoteToSellerSf({
            ...this.baseBiParams(cartModel),
            catalogAppId,
            checkoutId: this.viewerContext.isExperimentEnabled(SPECS.CallCartV2FromCartComponents)
              ? undefined
              : cartModel.checkoutId,
          })
        );
  };

  public viewCartPageSf({
    cartModel,
    shouldShowCoupon,
    shouldShowBuyerNote,
    shouldShowContinueShopping,
    shouldShowShipping,
    shouldShowTax,
    hasPickupOption,
    cartLayout,
    isCheckoutButtonPresented,
    isViewCartButtonPresented,
    hasGiftCardLineItem,
  }: {
    cartModel: CartModel;
    shouldShowCoupon: boolean;
    shouldShowBuyerNote: boolean;
    shouldShowContinueShopping: boolean;
    shouldShowShipping: boolean;
    shouldShowTax: boolean;
    hasPickupOption: boolean;
    cartLayout: string;
    isCheckoutButtonPresented: boolean;
    isViewCartButtonPresented: boolean;
    hasGiftCardLineItem: boolean;
  }): void {
    const biParams = {
      ...this.baseBiParams(cartModel),
      itemType: getItemTypes(cartModel),
      checkoutType: 'viewer',
      isContinueShopping: shouldShowContinueShopping,
      isDeliveryRates: shouldShowShipping,
      isPickupAvailable: hasPickupOption,
      isTaxCosts: shouldShowTax,
      is_note: shouldShowBuyerNote,
      is_promo_code: shouldShowCoupon,
      itemsCount: getItemsCount(cartModel),
      lineItemsCount: cartModel?.lineItems.length || 0,
      viewMode: this.viewerContext.getViewMode(),
      savePaymentMethod: cartModel?.lineItems?.some((lineItem) => lineItem.paymentConfig.savePaymentMethod),
      isCheckoutButtonPresented: cartLayout === CartLayout.SIDE_CART ? isCheckoutButtonPresented.toString() : 'true',
      isViewCartButtonPresented: cartLayout === CartLayout.SIDE_CART ? isViewCartButtonPresented.toString() : 'false',
      isPromoCodeDisabled:
        this.viewerContext.isExperimentEnabled(SPECS.GiftCardAddToCartSettings) && hasGiftCardLineItem,
    };
    this.biLogger.report(
      viewCartPageSf({
        ...biParams,
        cartLayout,
        catalogAppId: getCatalogAppIds(cartModel),
        checkoutId: this.viewerContext.isExperimentEnabled(SPECS.CallCartV2FromCartComponents)
          ? undefined
          : cartModel.checkoutId,
        is_member: this.viewerContext.isExperimentEnabled(SPECS.GiftCardAddToCartSettings)
          ? this.controllerConfig.wixCodeApi.user.currentUser.loggedIn
          : !!this.siteStore.usersApi?.currentUser?.loggedIn,
      })
    );
  }

  private static setDestinationBIData(cartModel: CartModel) {
    const biData: SetDestinationBIData = {
      cartId: cartModel.id,
      itemType: getItemTypes(cartModel),
    };
    biData.shippingDestinationCountry = cartModel.deliveryInfo.address.country;
    biData.shippingDestinationState = cartModel.deliveryInfo.address.subdivision;
    return biData;
  }

  public cartEstimateTotalsErrorShown(cartModel: CartModel, cartLayout: string): void {
    this.biLogger.report(
      cartEstimateTotalsErrorShownSrc130Evid146({
        ...this.baseBiParams(cartModel),
        cartLayout,
      })
    );
  }

  public cartEstimateTotalsErrorClickOnTryAgain(cartModel: CartModel, cartLayout: string): void {
    this.biLogger.report(
      cartEstimateTotalsErrorClickOnTryAgainSrc130Evid147({
        ...this.baseBiParams(cartModel),
        cartLayout,
      })
    );
  }

  public shippingOptionsPresentedInCart(cartModel: CartModel, deliveryOptionsModel: DeliverySectionModel): void {
    this.biLogger.report(
      shippingShippingOptionsPresentedInCheckoutCartSrc130Evid183({
        ...this.baseBiParams(cartModel),
        checkoutId: this.viewerContext.isExperimentEnabled(SPECS.CallCartV2FromCartComponents)
          ? undefined
          : cartModel.checkoutId,
        numOptionsPresented: deliveryOptionsModel?.options?.length,
        shippingOptionsPresented: deliveryOptionsModel?.optionsJson,
        isPartial: deliveryOptionsModel?.options?.some((option) => option.isPartial),
      })
    );
  }

  public shippingOptionsSelectedInCart(
    cartModel: CartModel,
    deliveryOptionsModel: DeliverySectionModel,
    shippingOptionId: string
  ): void {
    this.biLogger.report(
      shippingShippingOptionsSelectedSrc130Evid186({
        ...this.baseBiParams(cartModel),
        checkoutId: cartModel.checkoutId,
        numOptionsPresented: deliveryOptionsModel?.options?.length,
        shippingOptionsPresented: deliveryOptionsModel?.optionsJson,
        isPartial: deliveryOptionsModel?.options?.some((option) => option.isPartial),
        shippingOptionId,
        shippingOptionIndex: deliveryOptionsModel?.options?.findIndex((option) => option.id === shippingOptionId),
      })
    );
  }

  public reportClickToOpenCloseMoreDetails({
    cartModel,
    lineItemId,
    action,
  }: {
    cartModel: CartModel;
    lineItemId: string;
    action: string;
  }): void {
    void this.biLogger.report(
      cartCheckoutClickToOpenCloseMoreDetailsSrc130Evid190({
        ...this.baseBiParams(cartModel),
        checkoutId: this.viewerContext.isExperimentEnabled(SPECS.CallCartV2FromCartComponents)
          ? undefined
          : cartModel.checkoutId,
        lineItemId,
        action,
      })
    );
  }

  public checkoutButtonPresentedToUoU(cartModel: CartModel, isButtonShown: boolean): void {
    void this.biLogger.report(
      cartCartSideCartPresentedToUoUSrc130Evid149({
        ...this.baseBiParams(cartModel),
        cartLayout: this.origin,
        isCheckoutButtonPresented: isButtonShown.toString(),
      })
    );
  }

  public cartButtonPresentedToUoU(cartModel: CartModel, isButtonShown: boolean): void {
    void this.biLogger.report(
      cartCartSideCartPresentedToUoUCartButtonSrc130Evid150({
        ...this.baseBiParams(cartModel),
        cartLayout: this.origin,
        isViewCartButtonPresented: isButtonShown.toString(),
      })
    );
  }

  public promoCodePresentedToUoU(cartModel: CartModel, isPromoCodeShown: boolean): void {
    this.biLogger.report(
      cartPromoCodePresentedToTheUoUInCartSideCartSrc130Evid156({
        ...this.baseBiParams(cartModel),
        cartLayout: this.origin,
        isPromoCodePresented: isPromoCodeShown,
      })
    );
  }

  public buyerNotePresentedToUoU(cartModel: CartModel, isBuyerNoteShown: boolean): void {
    this.biLogger.report(
      cartNotePresentedToTheUoUSrc130Evid157({
        ...this.baseBiParams(cartModel),
        cartLayout: this.origin,
        isnotepresented: isBuyerNoteShown,
      })
    );
  }

  public clickOnCheckout(bIData: clickOnCheckoutParams): void {
    this.biLogger.report(
      clickOnCheckout({
        ...bIData,
        is_member: this.controllerConfig.wixCodeApi.user.currentUser.loggedIn,
      })
    );
  }

  public shouldEstimateTotalsTestEvent({
    cartLayout,
    shouldCalculateShipping,
    shouldCalculateTax,
    isExpressCheckoutButtonShownInSomeBreakpointOrIsEditorMode,
    isCartWithDepositItem,
    editorMode,
    hasValidationsInCart,
    hasAdditionalFees,
    hasItemWithFreeTrial,
  }: {
    cartLayout: string;
    shouldCalculateShipping: boolean;
    shouldCalculateTax: boolean;
    isExpressCheckoutButtonShownInSomeBreakpointOrIsEditorMode: boolean;
    isCartWithDepositItem: boolean;
    editorMode: boolean;
    hasValidationsInCart: boolean;
    hasAdditionalFees: boolean;
    hasItemWithFreeTrial: boolean;
  }): void {
    this.biLogger.report(
      ecomTestBiSrc130Evid160({
        type: 'shouldEstimateTotals',
        data: JSON.stringify({
          cartLayout,
          shouldCalculateShipping,
          shouldCalculateTax,
          isExpressCheckoutButtonShownInSomeBreakpointOrIsEditorMode,
          isCartWithDepositItem,
          editorMode,
          hasValidationsInCart,
          hasAdditionalFees,
          hasItemWithFreeTrial,
        }),
      })
    );
  }

  public updateCartItemQuantity(cartModel: CartModel): void {
    this.biLogger.report(
      updatedCartItemQuantitySf({
        ...this.baseBiParams(cartModel),
        itemsCount: getItemsCount(cartModel),
        catalogAppId: getCatalogAppIds(cartModel),
      })
    );
  }

  public addCouponToCurrentCart(cartModel: CartModel, couponCode: string): void {
    this.biLogger.report(
      clickApplyCoupon({
        ...this.baseBiParams(cartModel),
        couponCode,
        catalogAppId: getCatalogAppIds(cartModel),
        itemType: getItemTypes(cartModel),
      })
    );
  }

  public removeLineItemFromCurrentCart(cartModel: CartModel): void {
    this.biLogger.report(
      removedProductFromCartSf({
        ...this.baseBiParams(cartModel),
        itemsCount: getItemsCount(cartModel),
        catalogAppId: getCatalogAppIds(cartModel),
      })
    );
  }

  public removeCouponFromCurrentCart(cartModel: CartModel, couponCode: string): void {
    this.biLogger.report(
      removeACoupon({
        ...this.baseBiParams(cartModel),
        couponCode,
        catalogAppId: getCatalogAppIds(cartModel),
        itemType: getItemTypes(cartModel),
      })
    );
  }

  public checkoutCartCartSummaryPresented(cartModel: CartModel, cartSummary: CartSummaryModel): void {
    void this.biLogger.report(
      checkoutCartCartSummaryPresentedSrc130Evid226({
        ...this.baseBiParams(cartModel),
        calculationId: cartSummary?.calculationId,
      })
    );
  }

  private createBILogger(controllerConfig: IWidgetControllerConfig, viewerContext: ViewerContext) {
    const bi = controllerConfig.platformAPIs.bi;
    const user = viewerContext.getViewMode() === 'Site' ? {visitor_id: bi.visitorId} : {uuid: bi.ownerId};
    return createOOIWebBILogger({
      biLoggerFactory: controllerConfig.platformAPIs.biLoggerFactory,
      user,
      biToken: bi.biToken,
      defaults: {
        storeId: viewerContext.getEcomPlatformInstanceId(),
        editorMode: viewerContext.getDeviceType() === 'Desktop' ? 'desktop' : 'mobile',
        isMerchant: false,
        appName: 'Stores',
        _msid: bi.metaSiteId,
      },
    });
  }
}
